import type { HelpSectionsMap } from './types';

export const itHelpSections: HelpSectionsMap = {
  start: {
    title: 'Per iniziare',
    intro: 'Guida rapida per iniziare in WERSTACK. Segui i passaggi in ordine per orientarti più velocemente.',
    blocks: [
      {
        title: 'Passo 1. Scegli un workspace',
        text: 'Un workspace è il tuo progetto con file, impostazioni e membri del team propri.',
        items: [
          'Nella barra laterale sinistra, apri «Miei» o «Condivisi» e seleziona un workspace.',
          'File e cartelle cambiano automaticamente.',
          '«Crea» in alto nella barra laterale — un nuovo workspace personale (se non hai raggiunto il limite).',
          'Ogni workspace può avere lingua, tema e colore di accento propri.',
        ],
        where: 'Barra laterale sinistra → «Miei» / «Condivisi». Se è compressa, passa il mouse sull\'icona di una sezione per vedere l\'elenco.',
      },
      {
        title: 'Passo 2. Carica i primi file',
        items: [
          'Fai clic su «Carica immagini» nella barra degli strumenti della galleria.',
          'Ctrl+Shift+V — incolla uno screenshot dagli appunti nella cartella corrente.',
          'Fino a 1000 file alla volta (JPEG, PNG, WebP; fino a 10 MB ciascuno).',
          'In basso compare una barra di avanzamento — puoi chiuderla o annullarla.',
        ],
        where: 'Schermata principale → pulsante «Carica immagini» in alto a destra.',
      },
      {
        title: 'Passo 3. Crea cartelle',
        items: [
          'Fai clic su «Crea cartella», inserisci un nome e conferma.',
          'Apri una cartella con doppio clic (tocco singolo sui dispositivi touch).',
          'Alla radice del workspace, la barra degli strumenti mostra il nome del workspace a sinistra.',
          'In una sottocartella — breadcrumb con icona home per tornare alla radice.',
        ],
        where: 'Schermata principale → «Crea cartella» accanto al caricamento.',
      },
      {
        title: 'Barra laterale',
        items: [
          'Logo WERSTACK ed elenchi workspace in alto.',
          '«Crea» — nuovo workspace.',
          '«Dati» — tabelle: «Tutte» apre il catalogo; elenchi sotto per l\'apertura rapida.',
          '«Bacheche» — kanban sulle tabelle.',
          '«Workflow» — grafi di automazione dello spazio attuale.',
          '«Gallerie» — catalogo delle gallerie pubbliche.',
          'Pulsante sul bordo — comprimi o espandi la barra laterale.',
        ],
        where: 'Colonna sinistra della schermata principale.',
      },
      {
        title: 'Pulsanti della barra superiore',
        items: [
          'Campanella — inviti al team e notifiche sui commenti.',
          'Punto interrogativo — questo pannello di aiuto (separato dal profilo).',
          'Iniziali — esci.',
        ],
        where: 'Angolo in alto a destra dello schermo.',
      },
      {
        title: 'Ruoli e permessi',
        text: 'Non tutte le azioni sono disponibili per ogni utente.',
        items: [
          'Proprietario e Admin — accesso completo, gestione di membri e gallerie.',
          'Editor — visualizza e modifica i file, senza gestione del team.',
          'Visualizzatore — sola visualizzazione, niente caricamento o eliminazione.',
        ],
        tip: 'Se un pulsante è disabilitato o una voce di menu manca, potresti avere il ruolo Visualizzatore.',
      },
    ],
  },
  gallery: {
    title: 'Galleria: file e cartelle',
    intro: 'La schermata principale dove le immagini sono archiviate e organizzate.',
    blocks: [
      {
        title: 'Barra degli strumenti',
        items: [
          'A sinistra — nome del workspace (alla radice), breadcrumb (in una sottocartella) o indicatore di ricerca/filtro attivo.',
          'A destra — caricamento, crea cartella, ricerca, filtro tag, ordinamento, vista griglia/elenco, cestino.',
        ],
        where: 'Barra sotto l\'intestazione superiore dell\'app.',
      },
      {
        title: 'Modalità di visualizzazione',
        items: [
          'Griglia — schede con anteprima, like e indicatore commenti.',
          'Elenco — tabella: nome, data, tipo, dimensione; utile per la selezione multipla.',
        ],
        where: 'Pulsante di cambio vista (icone griglia ed elenco) a destra nella barra degli strumenti.',
      },
      {
        title: 'Ricerca file',
        items: [
          'Fai clic sull\'icona della lente e digita almeno 2 caratteri.',
          'La ricerca copre nome file, tag e testo dei commenti.',
          'Per uscire dalla ricerca — fai clic su «Cancella ricerca» o svuota il campo.',
        ],
        where: 'Icona lente nella barra degli strumenti. Ordinamento e caricamento sono nascosti durante la ricerca.',
      },
      {
        title: 'Filtro tag',
        items: [
          'Fai clic sull\'icona tag e seleziona uno o più tag.',
          'I tag selezionati compaiono come chip a sinistra — rimuovi qualsiasi tag con ×.',
          'Con tag attivi, compare «Crea una galleria per tag» (vedi Gallerie pubbliche).',
        ],
        where: 'Icona tag nella barra degli strumenti.',
      },
      {
        title: 'Ordinamento',
        items: [
          'Per data di caricamento (più recenti o più vecchi per primi).',
          'Per nome (A–Z o Z–A).',
          'Per dimensione file.',
          'Per numero di like.',
        ],
        where: 'Icona controlli a scorrimento nella barra degli strumenti. Non disponibile nel cestino, in ricerca o con filtro tag.',
      },
      {
        title: 'Like',
        items: [
          'Fai clic sul cuore su una scheda per mettere o togliere il like.',
          'Il like non apre il file — fai clic sull\'anteprima o sul nome per aprirlo.',
        ],
      },
      {
        title: 'Paginazione',
        text: 'Quando ci sono molti file, usa il pannello in basso per cambiare pagina e elementi per pagina.',
        where: 'Barra di paginazione in fondo allo schermo.',
      },
    ],
  },
  selection: {
    title: 'Selezione e azioni',
    intro: 'Come selezionare più file e cartelle e cosa puoi farne.',
    blocks: [
      {
        title: 'Selezione con il mouse',
        items: [
          'Clic su un elemento — aggiungi o rimuovi dalla selezione.',
          'Shift + clic — seleziona un intervallo dall\'ultimo elemento selezionato.',
          'Trascina un rettangolo su uno spazio vuoto — seleziona tutto ciò che è dentro.',
          'Ctrl (Cmd su Mac) + rettangolo — aggiungi alla selezione corrente.',
          'Ctrl+A — seleziona tutto nella pagina.',
          'Escape o clic fuori — annulla la selezione.',
        ],
      },
      {
        title: 'Selezione touch',
        items: [
          'Tocco singolo — apri file o cartella.',
          'Doppio tocco su una scheda — aggiungi alla selezione.',
          'Trascina su spazio vuoto (rubber band) — selezione multipla.',
          'Pressione prolungata su spazio vuoto — menu contestuale.',
        ],
      },
      {
        title: 'Trascina e rilascia',
        items: [
          'Trascina un file o una cartella su un\'altra cartella per spostarlo.',
          'Se l\'elemento è già selezionato, si sposta l\'intera selezione.',
          'Su smartphone, il trascinamento touch mostra una scheda di anteprima flottante.',
        ],
        tip: 'Il trascina e rilascia è disabilitato nel cestino.',
      },
      {
        title: 'Taglia e incolla',
        items: [
          'Seleziona elementi → «Taglia» nel menu contestuale o Ctrl+X.',
          'Vai alla cartella di destinazione → «Incolla» o Ctrl+V.',
          'Non puoi incollare nella stessa cartella da cui hai tagliato.',
        ],
      },
      {
        title: 'Menu contestuale',
        text: 'Clic destro sulla galleria (o pressione prolungata su touch) per le azioni.',
        items: [
          'Carica immagini / Crea cartella.',
          'Cut, Paste.',
          'Rename folder (esattamente una cartella selezionata).',
          'Crea galleria (esattamente una cartella selezionata).',
          'Aggiungi etichetta (solo file nella selezione).',
          'Genera — immagini selezionate (editor e admin).',
          'Delete (sposta nel cestino).',
          'Ripristina (solo nel cestino).',
        ],
        tip: 'Accanto alle voci di menu compaiono i suggerimenti delle scorciatoie.',
      },
      {
        title: 'Operazioni multiple',
        items: [
          'Elimina selezione — nel cestino (con conferma).',
          'Aggiungi etichetta — a tutti i file selezionati in una volta.',
          'Taglia e incolla — sposta un gruppo di file.',
        ],
      },
      {
        title: 'Generazione immagine con IA (galleria)',
        items: [
          'Seleziona una o più immagini → menu contestuale → Genera.',
          'Un\'immagine nuova per ogni file selezionato, una dopo l\'altra. Scarica o salva il risultato — gli originali non cambiano.',
        ],
      },
    ],
  },
  viewer: {
    title: 'Visualizzatore file',
    intro: 'Finestra a schermo intero con info file, commenti e azioni.',
    blocks: [
      {
        title: 'Come aprire',
        items: [
          'Fai clic su una scheda file nella galleria (o sul nome in vista elenco).',
          'In modalità selezione multipla — doppio clic / doppio tocco.',
        ],
      },
      {
        title: 'Naviga tra i file',
        items: [
          'Frecce Previous / Next ai lati della finestra.',
          'Tasti ← e → sulla tastiera.',
          'Escape — chiudi (o esci dalla vista immagine a schermo intero).',
        ],
      },
      {
        title: 'Scheda Info',
        items: [
          'Nome, risoluzione, dimensione, data di caricamento.',
          'Tag — fai clic su un tag per filtrare la galleria.',
          'Pulsanti: Modifica, Scarica, Crea/revoca collegamento pubblico, Copia URL.',
        ],
        where: 'Pannello destro del modale → scheda Info.',
      },
      {
        title: 'Tag',
        items: [
          'Icona tag nell\'angolo — aggiungi un nuovo tag.',
          '× su un chip tag — rimuovi il tag.',
        ],
      },
      {
        title: 'Generazione immagine con IA',
        items: [
          'Icona scintille accanto a Tag — descrivi lo sfondo e genera una nuova immagine prodotto dal file corrente.',
          'L\'anteprima resta nella modale: scarica o salva come nuovo file in una cartella (l\'originale non viene sovrascritto).',
        ],
        where: 'Barra del visualizzatore → icona scintille (editor e admin).',
      },
      {
        title: 'Link pubblico per un file',
        items: [
          '«Crea collegamento» — chiunque abbia il link può visualizzare senza accedere.',
          '«Revoca collegamento» — l\'accesso viene revocato.',
          '«Copia URL» — copia il link negli appunti.',
        ],
        tip: 'Questo è un link per un singolo file. Per gli album usa Gallerie pubbliche.',
      },
      {
        title: 'Scheda Comments',
        items: [
          'Scrivi commenti e risposte sul file.',
          'Modifica ed elimina i tuoi commenti.',
          'Aggiungi reazioni emoji ai commenti.',
          'Menziona i colleghi con @ — inizia a digitare nome o email.',
        ],
        where: 'Pannello destro → scheda Comments. Gli avvisi sui nuovi commenti compaiono nella campanella.',
      },
      {
        title: 'Vista a schermo intero',
        text: 'Fai clic sull\'icona di espansione sull\'immagine per visualizzarla a schermo intero.',
      },
    ],
  },
  editor: {
    title: 'Editor immagini',
    intro: 'Apri un file dal visualizzatore con «Modifica». Il canvas è composto da livelli: frame, immagini, testo e forme. Tutte le modifiche possono essere annullate.',
    blocks: [
      {
        title: 'Barra superiore',
        items: [
          'Zoom in / out / fit to screen — scala del canvas.',
          'Undo / Redo — cronologia delle modifiche.',
          'Reset — ripristina l\'immagine originale.',
          'Sono mostrati lo zoom corrente (%) e le dimensioni del frame in pixel.',
        ],
      },
      {
        title: 'Livelli e proprietà',
        items: [
          'Pannello livelli (sinistra) — elenco di frame, immagini, testo e forme; seleziona, riordina, blocca, elimina.',
          'Pannello proprietà (destra) — impostazioni del livello selezionato.',
          'Fai clic su un livello sul canvas o nell\'elenco per modificarlo.',
          'Add image — livelli immagine aggiuntivi dal tuo workspace.',
          'Ctrl+C — copia il livello selezionato (immagine, sticker, testo o forma). Il frame non può essere copiato.',
          'Ctrl+V — incolla il livello copiato con un leggero offset.',
        ],
        where: 'Pulsanti della barra degli strumenti con icone livelli e controlli a scorrimento.',
      },
      {
        title: 'Frame (layout)',
        items: [
          'Dimensione, sfondo, raggio angoli e bordo.',
          'Imposta rapporto d\'aspetto — scegli un preset (1:1, 16:9, 4:3, ecc.) per il frame.',
          'Quando cambia il rapporto d\'aspetto del frame, testo e forme restano al posto e mantengono la dimensione; cambia solo l\'area visibile del frame.',
          'Il livello immagine mantiene la posizione visiva; puoi ridimensionarlo o spostarlo dopo come al solito.',
        ],
        where: 'Pannello proprietà (destra) quando il livello frame è selezionato.',
        tip: 'Premi Escape per uscire dalla modalità rapporto d\'aspetto.',
      },
      {
        title: 'Livello immagine',
        items: [
          'Seleziona il livello immagine — il pannello proprietà mostra «Immagine».',
          'Posizione e dimensione — campi numerici; rotazione — cursore (−180°…+180°) nella sezione Rotazione.',
          'Clic destro sull\'immagine → modalità ridimensionamento; trascina le maniglie degli angoli. Trascina dentro l\'immagine per spostarla nel frame.',
          'Sezione Trasformazione — Modifica con IA (barra prompt in basso sul canvas, fino a 200 caratteri), Rimuovi lo sfondo (usa il pool condiviso di token IA), raddrizza (compare un cursore angolo in basso; fai clic su Fatto), capovolgi orizzontale / verticale.',
          'Sezione Trasformazione — Ritaglia (rettangolo libero + Taglia), Lazo (elimina selezione o mantieni solo la selezione + segno di spunta per applicare).',
          'Sezioni Colore ed Effetti — luminosità, contrasto, saturazione e preset filtri.',
        ],
        where: 'Pannello proprietà (destra) quando un livello immagine è selezionato.',
        tip: 'Modifica con IA invia il livello così com\'è (rotazione, capovolgimento, filtri) e lo sostituisce sul canvas. Escape esce da ridimensionamento, raddrizzamento, ritaglio, lazo o dal prompt IA. Il doppio tocco chiude il percorso del lazo sui dispositivi touch.',
      },
      {
        title: 'Stickers',
        items: [
          'Il pulsante «Aggiungi adesivo» nella barra degli strumenti apre la libreria sticker del workspace.',
          'Un nuovo sticker viene creato quando usi Rimuovi lo sfondo su un livello immagine — il risultato viene salvato nella libreria e applicato al livello corrente.',
          'Fai clic su uno sticker nel pannello o trascinalo sul canvas per aggiungerlo come livello separato.',
          'Gli sticker si comportano come le immagini: posizione, dimensione, rotazione, ombra; compaiono con un\'icona dedicata nel pannello livelli.',
          'Il × su una scheda della libreria rimuove permanentemente lo sticker dal workspace (i livelli già sul canvas non sono interessati).',
        ],
        where: 'Barra degli strumenti → icona sticker; pannello sticker a sinistra (accanto al pannello livelli).',
        tip: 'Rimuovi lo sfondo usa il livello così com\'è, inclusi rotazione, capovolgimento, scala e filtri.',
      },
      {
        title: 'Livelli di testo',
        items: [
          'Aggiungi un livello titolo o corpo testo dalla barra degli strumenti.',
          'Font, dimensione, colore, contorno, ombra, sfondo, spaziatura.',
          'Allineamento — orizzontale (left / center / right) e verticale (top / center / bottom) dentro la casella di testo.',
          'Trascina il testo sul canvas; ridimensiona la casella con le maniglie degli angoli.',
          '«Applica all\'immagine» — incorpora il testo nel bitmap prima del salvataggio.',
        ],
        where: 'Su mobile, apri il pannello proprietà con l\'icona controlli a scorrimento.',
      },
      {
        title: 'Forme',
        items: [
          'Rectangle, triangle, circle, line — scegli uno strumento e trascina sul canvas per disegnare.',
          'Riempimento, contorno, raggio angoli, ombra e gradiente nelle proprietà.',
          'Trascina per spostare; maniglie angolo per ridimensionare; maniglia superiore per ruotare.',
          'Escape annulla lo strumento di disegno attivo.',
        ],
      },
      {
        title: 'Navigazione canvas',
        items: [
          'Mouse: trascina per spostare; rotella per zoom.',
          'Touch: pinch per zoom; un dito per spostare.',
        ],
      },
      {
        title: 'Salvataggio',
        items: [
          'Salva — sovrascrivi il file originale.',
          'Scarica — salva il file sul computer.',
          'Salva con nome — nuovo file (nome, PNG/JPG/WebP, dimensione, cartella).',
        ],
        where: 'Pulsante Salva e menu a freccia a destra dell\'intestazione dell\'editor.',
      },
    ],
  },
  publicGalleries: {
    title: 'Gallerie pubbliche',
    intro: 'Pagine eleganti per mostrare il lavoro ai clienti — nessun account richiesto.',
    blocks: [
      {
        title: 'Galleria da una cartella',
        items: [
          'Seleziona esattamente una cartella.',
          'Menu contestuale → «Crea galleria» (o Ctrl+Alt+G).',
          'Imposta nome, layout, theme e info autore.',
          'Copia il link e invialo al cliente.',
        ],
      },
      {
        title: 'Galleria per tag',
        items: [
          'Attiva il filtro per uno o più tag.',
          'Fai clic su «Crea una galleria per tag».',
          'Il contenuto si aggiorna automaticamente quando cambi i tag dei file.',
        ],
        where: 'Il pulsante compare nella barra degli strumenti quando il filtro tag è attivo.',
      },
      {
        title: 'Impostazioni galleria',
        items: [
          'Layout: grid, masonry o large cards.',
          'Theme: chiaro/scuro e colore di accento.',
          'Genera un biglietto da visita con codice QR.',
        ],
      },
      {
        title: 'Pannello autore',
        items: [
          'Modalità «Autore» — nome, professione, contatti e descrizione.',
          'Modalità «Azienda» — nome, logo dai file del workspace, slogan, contatti e «Informazioni sull\'azienda».',
          'Cambiare modalità non cancella i dati dell\'altra modalità.',
          'I campi compilati compaiono nel pannello laterale della pagina pubblica e sul biglietto da visita digitale.',
        ],
        where: 'Sezione «Autore» nella finestra di creazione o modifica galleria.',
      },
      {
        title: 'Gestisci gallerie',
        items: [
          'Barra laterale → «Gallerie» — catalogo di tutte le gallerie pubbliche per spazio.',
          'Apri, modifica, elimina o mostra il biglietto QR — azioni sulla scheda.',
        ],
        where: 'Barra laterale → «Gallerie» (pagina /galleries).',
      },
      {
        title: 'Cosa vedono i visitatori',
        items: [
          'Griglia immagini con scroll infinito.',
          'Vista a schermo intero (Escape, ← →).',
          'Pannello autore con dati della persona o dell\'azienda e contatti.',
          'Scarica il biglietto da visita digitale con codice QR alla galleria.',
        ],
      },
    ],
  },
  profile: {
    title: 'Profilo e team',
    intro: 'Impostazioni personali, workspace e membri del team.',
    blocks: [
      {
        title: 'Apri profilo',
        items: [
          'Voce «Profilo» in fondo alla barra laterale.',
          '«Esci» — termina sessione (menu iniziali, in alto a destra).',
        ],
        tip: 'Il profilo si apre come pagina completa; l\'aiuto (punto interrogativo) è un pannello separato.',
      },
      {
        title: 'Scheda Personale',
        items: [
          'Modifica nome e email.',
          'Fai clic su «Salva modifiche».',
        ],
      },
      {
        title: 'Scheda Token IA',
        items: [
          'Mostra i token usati e rimasti del pool condiviso (rimozione sfondo, generazione immagini, modifica IA).',
          'Quando i token finiscono, le azioni IA sono disabilitate finché un admin non alza il tuo limite.',
        ],
        where: 'Profilo → Token IA.',
      },
      {
        title: 'Scheda Sicurezza',
        items: [
          'Cambia password: nuova password e conferma.',
          'La password deve rispettare i requisiti di complessità.',
        ],
      },
      {
        title: 'Scheda Workspaces',
        items: [
          'Crea un nuovo workspace (nome, lingua, theme, accento).',
          'Modifica o elimina il tuo workspace.',
          'Lascia il workspace di qualcun altro.',
          'Visualizza l\'utilizzo dello storage.',
        ],
      },
      {
        title: 'Scheda Team',
        items: [
          'Elenco membri per ciascuno dei tuoi workspace, con posti usati / max (il limite include il proprietario).',
          'Cambia ruolo: Admin, Editore, Visualizzatore (⋮ → Modifica ruolo).',
          'Assegna visibilità: nascondi cartelle, tabelle o board a un membro (⋮ → Assegna visibilità).',
          'Rimuovi un membro dal workspace.',
        ],
      },
      {
        title: 'Visibilità',
        items: [
          'La visibilità è una deny-list oltre al ruolo: una risorsa nascosta resta inaccessibile anche se il ruolo la consentirebbe.',
          'Il proprietario può nascondere risorse a qualsiasi membro. L\'admin solo a Editore e Visualizzatore — non al proprietario né a un altro admin.',
          'Nascondere una cartella nasconde anche sottocartelle e file nell\'app (elenchi, ricerca, apertura via URL).',
          'Tabelle e board si nascondono separatamente dalle cartelle.',
          'In invito: dopo il ruolo puoi impostare nascoste opzionali e poi inviare.',
          'I link pubblici ai file funzionano ancora anche se il file è in una cartella nascosta.',
          'La cartella root del workspace non può essere nascosta.',
        ],
        where: 'Profilo → Team → ⋮ su un membro → Assegna visibilità. Oppure campanella → Inviato → wizard di invito.',
        tip: 'Le nascoste valgono solo in quel workspace e non in altri.',
      },
    ],
  },
  datasets: {
    title: 'Tabelle',
    intro: 'Tabelle in stile CRM nei tuoi spazi — colonne, righe e modifica inline.',
    blocks: [
      {
        title: 'Dove trovare le tabelle',
        items: [
          'Barra laterale → « Dati » → « Tutte » — catalogo di tutte le tabelle per spazio (pagina /data).',
          'Barra laterale → « Dati »: elenco rapido delle tue tabelle e di quelle condivise.',
          '« Crea » sotto Dati — nuova tabella nello spazio corrente.',
          'Nella pagina catalogo — apri, modifica, elimina, esporta o vedi lo schema dalle azioni della scheda.',
        ],
        where: 'Barra laterale sinistra → Dati (e « Tutte » per il catalogo completo).',
      },
      {
        title: 'Aprire una tabella',
        items: [
          'Clic sul nome nella barra laterale o su una scheda in /data — griglia CRM su /data/:id.',
          'Scorrendo si caricano altre righe (100 per batch).',
          'La posizione di scroll viene ricordata al ritorno sulla stessa tabella.',
        ],
      },
      {
        title: 'Colonne e schema',
        items: [
          '+ nell\'intestazione — aggiungi colonna (nome, tipo, opzioni).',
          'Trascina la maniglia — riordina colonne.',
          'Trascina il bordo destro — ridimensiona; larghezze salvate localmente.',
          'Clic sul nome colonna — rinomina (se puoi modificare lo schema).',
          'Menu ⋮ — filtro testo, ordinamento crescente o decrescente, blocca colonna durante lo scroll orizzontale, azzera ordinamento/filtro o elimina colonna.',
          'Catalogo /data → modifica su una scheda — nome, descrizione e colonne.',
        ],
      },
      {
        title: 'Ordinamento e filtro',
        text: 'Ogni tabella memorizza un ordinamento e un filtro testo per la tua vista. Le impostazioni restano nel browser e si applicano al caricamento delle righe — scroll infinito e download inclusi.',
        items: [
          'Apri ⋮ nell\'intestazione colonna (sempre visibile su tablet e mobile).',
          'Ordine crescente o decrescente — una freccia piccola nel titolo indica la direzione.',
          'Si può ordinare solo per una colonna; «Azzera ordinamento» nello stesso menu.',
          'Filtro — digita testo e clicca «Applica» (o Invio) per righe dove la colonna contiene quel testo.',
          'Con filtro attivo, accanto al titolo compare una piccola icona filtro (come per l\'ordinamento).',
          '«Rimuovi filtro» per resettare; cambiare ordine o filtro ricarica le righe dal server.',
        ],
        where: 'Intestazione colonna → menu ⋮.',
        tip: 'Ordine e filtro sono salvati per tabella su questo dispositivo e usati nell\'export dal catalogo tabelle.',
      },
      {
        title: 'Elenchi di riferimento',
        text: 'Un elenco di riferimento è una piccola « tabella-dizionario » nello spazio: stati, categorie, SKU e altri valori che preferisci tenere in un posto solo, invece di copiarli in ogni tabella.',
        items: [
          'Creazione: barra laterale → « Dati » → « Crea » → attiva « Crea elenco di riferimento ». Compila a mano o importa subito da CSV/Excel.',
          'All\'interno c\'è una sola colonna di valori univoci. Funziona come una tabella normale: aggiungi, modifica ed elimina righe nella griglia.',
          'Catalogo /data → matita sull\'elenco: nome, descrizione, rinomina la colonna chiave, sezione « Valori dell\'elenco ».',
          'Lì c\'è anche il download — l\'export rispetta filtro e ordinamento impostati nella griglia.',
          'Import in un elenco esistente: aprilo → « Importa CSV/Excel » → scegli la colonna del file con le chiavi; i duplicati nel file vengono ignorati.',
          'Nella barra laterale e nella pagina catalogo gli elenchi hanno il badge « List ».',
        ],
        where: 'Creazione: barra laterale → Dati → Crea. Gestione: barra laterale → Dati → Tutte (/data).',
        tip: 'Un elenco per spazio = una fonte unica per stati, stagioni, SKU e simili.',
      },
      {
        title: 'Colonna « Riferimento » in una tabella',
        text: 'Quando una cellula deve scegliere da un elenco condiviso — non digitare testo libero — aggiungi una colonna di tipo Riferimento e collegala all\'elenco giusto.',
        items: [
          'Elenco (select) — le opzioni restano solo in quella colonna. Va bene per poche scelte locali.',
          'Riferimento — i valori arrivano da un elenco dello spazio. Utile quando lo stesso insieme serve in più tabelle.',
          'Aggiungi colonna: « + » nell\'intestazione o catalogo /data → modifica tabella → Aggiungi colonna → tipo Riferimento → scegli l\'elenco.',
          'Nella cellula compare un menu a tendina con ricerca. Dopo il salvataggio, il valore resta legato alla voce dell\'elenco.',
          'Se rinomini un valore nell\'elenco, la tabella mostra l\'etichetta aggiornata.',
        ],
        where: 'Intestazione tabella → « + », oppure Dati → Tutte → modifica su una scheda.',
        tip: 'Crea e compila prima l\'elenco, poi aggiungi le colonne Riferimento nelle tabelle di lavoro.',
      },
      {
        title: 'Elenchi e file',
        text: 'Puoi collegare le righe della tabella ai file in galleria e aggiornare automaticamente i tag quando cambia una colonna Riferimento.',
        items: [
          'Passo 1 — Collegamento file: apri la tabella → « Collegamento file » → scegli la colonna per l\'abbinamento (spesso SKU o codice) e come si mappa ai tag.',
          'Poi la griglia mostra una colonna con anteprime dei file collegati; dal visualizzatore immagini vedi le righe correlate.',
          'Passo 2 — Azione file sulla colonna Riferimento: in creazione o modifica, imposta « Azione file » su Aggiungi tag o Sostituisci tag nell\'elenco.',
          'Scatta quando la riga ha già file collegati e modifichi quella cellula — i tag si aggiornano da soli (nome tag = valore dell\'elenco).',
          'Senza collegamento file configurato prima, le azioni sui tag non partono — collega prima tabella e galleria.',
        ],
        where: 'Tabella → Collegamento file; azione tag — nelle impostazioni della colonna Riferimento.',
        tip: 'Esempio: lo SKU unisce riga e foto prodotto; cambiando lo stato nell\'elenco, i file ricevono tag « In vendita » o « Archivio ».',
      },
      {
        title: 'Unione tabelle',
        text: 'Combina due tabelle esistenti in una vista di sola lettura. Le righe vengono abbinate per colonne di collegamento — utile per arricchire dati da un\'altra tabella.',
        items: [
          'Barra laterale → « Dati » → « Unisci » — procedura guidata in tre passaggi.',
          'Passo 1: nome e spazio per la tabella unita.',
          'Passo 2: tabella principale e secondaria, poi colonne di collegamento (nella seconda, la colonna di collegamento deve essere univoca).',
          'Passo 3: scegliere quali colonne di ciascuna tabella mostrare nel risultato.',
          'Le tabelle unite si aprono come una griglia normale, ma righe e schema non sono modificabili — solo ordine colonne, filtri e ordinamento.',
          'Nella barra laterale e nel catalogo /data, le tabelle unite mostrano il badge « Merge ».',
        ],
        where: 'Barra laterale sinistra → Dati → Unisci (accanto a Crea).',
        tip: 'Prepara prima entrambe le tabelle di origine con una colonna chiave condivisa (es. SKU o ID) contrassegnata come univoca nella seconda tabella.',
      },
      {
        title: 'Righe e celle',
        items: [
          'Clic sulla cella per modificare; Invio salva, Esc annulla.',
          'Tab / Maiusc+Tab — cella successiva / precedente.',
          '« Aggiungi riga » in basso — nuova riga (bozza vuota annullata cliccando fuori).',
          'Cestino sulla riga — elimina una riga.',
          'Checkbox + ⋮ in intestazione — elimina selezionate in blocco.',
        ],
        tip: 'Le modifiche appaiono subito; l\'app sincronizza con il server in background.',
      },
      {
        title: 'Permessi',
        items: [
          'Proprietario / Admin — schema, righe e metadati.',
          'Editor — solo righe.',
          'Viewer — sola lettura.',
        ],
      },
    ],
  },
  boards: {
    title: 'Bacheche',
    intro: 'Bacheche Kanban sulle tabelle: colonne da un elenco di riferimento (stati), schede dalle righe.',
    blocks: [
      {
        title: 'Dove trovare le bacheche',
        items: [
          'Barra laterale → « Bacheche » → « Tutte » — catalogo per spazio (pagina /boards).',
          'Barra laterale → « Bacheche »: elenco rapido delle tue bacheche e di quelle condivise.',
          '« Crea » sotto « Bacheche » — nuova bacheca nello spazio corrente.',
          'Nel catalogo — apri, modifica le impostazioni o elimina dal menu della scheda.',
        ],
        where: 'Barra laterale sinistra → Bacheche (e « Tutte » per il catalogo completo).',
      },
      {
        title: 'Creare una bacheca',
        items: [
          'Passo 1: nome, descrizione opzionale e spazio di lavoro.',
          'Passo 2: tabella idonea e colonna di riferimento per gli stati; scegli quali valori diventano colonne (almeno uno).',
          'Passo 3: campo titolo e campi extra opzionali sulle schede.',
          'Dopo la creazione si apre /boards/:id.',
        ],
        tip: 'Solo tabelle normali con colonna di riferimento. Merge e list non compaiono nella procedura guidata.',
      },
      {
        title: 'Bacheca Kanban',
        items: [
          'Le colonne seguono l\'ordine degli stati; trascina la maniglia per riordinare (admin).',
          'Le schede mostrano il titolo e alcuni campi di anteprima.',
          'Un\'icona messaggio con conteggio appare sulla scheda se ci sono commenti.',
          'Un\'icona graffetta con conteggio appare se ci sono file allegati.',
          'Trascina le schede tra colonne o dentro una colonna — cambiano stato e ordine.',
          'Aggiornamenti live via SSE — gli altri utenti vedono spostamenti, modifiche, commenti e allega/scollega file senza ricaricare.',
        ],
      },
      {
        title: 'Schede',
        items: [
          'Clic su una scheda — modale di visualizzazione con campi, scheda Commenti e scheda File.',
          '⋮ → Modifica — pannello laterale della riga.',
          '⋮ → Elimina — rimuove la riga dopo conferma.',
          '« Aggiungi scheda » sotto una colonna — nuova riga con quello stato.',
        ],
        where: 'Pagina bacheca → scheda o piè di colonna.',
        tip: 'Il link /boards/:id?card=:rowId apre la stessa modale (es. dalla campanella).',
      },
      {
        title: 'Commenti sulle schede',
        items: [
          'I commenti appartengono a questa bacheca + scheda (riga), non a tutte le bacheche sulla stessa riga.',
          'Chiunque con accesso al workspace (viewer+) può commentare; autore o admin possono eliminare i commenti altrui.',
          'I nuovi commenti notificano i membri nella campanella.',
        ],
      },
      {
        title: 'File sulle schede',
        items: [
          'Scheda File — allega immagini dallo spazio della bacheca (stesso selettore dell\'editor immagini).',
          'Gli allegati appartengono a questa bacheca + scheda (riga), non a tutte le bacheche sulla stessa riga.',
          'Clic sull\'anteprima apre il visualizzatore; al passaggio del mouse si vede il nome; ✕ scollega senza eliminare il file dalla galleria.',
          'Se il file è nel Cestino, l\'allegato resta con overlay «nel cestino»; l\'eliminazione definitiva rimuove l\'allegato.',
          'Chiunque con accesso al workspace (viewer+) può allegare e scollegare.',
        ],
        where: 'Modale scheda → scheda File.',
      },
      {
        title: 'Colonne',
        items: [
          'Admin: aggiungi colonna (valore di stato nuovo o esistente), rinomina, riordina o rimuovi.',
          'Una colonna con schede non si può rimuovere — sposta o elimina prima le schede.',
          'Rimuovere una colonna la nasconde solo su questa bacheca; il valore resta nell\'elenco di riferimento.',
        ],
      },
      {
        title: 'Assegnatari e scadenze',
        items: [
          'Apri una scheda → Assegnatari: aggiungi membri del workspace, ciascuno con la propria scadenza e colonna di destinazione.',
          'Solo Proprietario / Admin possono assegnare, modificare scadenze/colonne o impostare la priorità (dal più basso al più alto).',
          'Editor vede tutti gli assegnatari; Viewer vede solo la propria assegnazione sulla scheda.',
          'Trascinare la scheda nella colonna di destinazione di un assegnatario completa il suo compito; riportarla indietro lo riapre.',
          'Più persone con la stessa colonna di destinazione si completano con un solo spostamento.',
          'Le assegnazioni scadute sono evidenziate sulla scheda; gli avvisi arrivano nella campanella al login / refresh del token (niente cron).',
          'Avvisi personali per le tue scadenze (presto / oggi / scaduto). Proprietario/Admin ricevono anche un digest se qualcun altro è in ritardo.',
        ],
        where: 'Bacheca → modale scheda → Assegnatari (e Priorità sotto i campi).',
        tip: 'Cambiare la scadenza azzera lo stato degli avvisi — il prossimo login/refresh potrà notificare di nuovo.',
      },
      {
        title: 'Permessi',
        items: [
          'Viewer — vedere la bacheca, trascinare le schede, commentare e allegare/scollegare file; vede solo i propri assegnatari.',
          'Editor — creare, modificare ed eliminare schede (righe); vede tutti gli assegnatari ma non può assegnare.',
          'Proprietario / Admin — impostazioni, colonne, eliminazione, moderazione commenti, assegnazioni, scadenze/colonne e priorità.',
        ],
      },
    ],
  },
  workflows: {
    title: 'Workflow',
    intro: 'I workflow sono un livello di prodotto accanto a File, Dati, Bacheche e Gallerie — non una schermata di impostazioni. Costruisci un grafo di automazione che muove file, righe e schede nei sistemi già configurati.',
    blocks: [
      {
        title: 'Non sono impostazioni',
        items: [
          'I workflow vivono in una sezione dedicata della barra laterale — come Dati o Bacheche, non sotto Profilo / impostazioni.',
          'Ogni processo appartiene a uno spazio e usa cartelle, tabelle, bacheche, pattern e tag di quello spazio.',
          'Abilita avvia il processo; Salva scrive il grafo. Le esecuzioni mostrano cosa è successo.',
        ],
        tip: 'Lingua, tema e account sono nel Profilo, non nei Workflow.',
      },
      {
        title: 'Dove trovare i workflow',
        items: [
          'Barra laterale → «Workflow» → «Tutti» — catalogo per spazio (/workflows).',
          'Barra laterale → «Workflow» → «Attuali» — elenco rapido dello space attivo (nascosto se vuoto).',
          '«Crea» sotto Workflow — nuovo grafo (solo owner/admin).',
          'Apri un processo — builder a grafo; Esecuzioni — journal.',
        ],
        where: 'Barra laterale sinistra → Workflow.',
      },
      {
        title: 'Builder a grafo',
        items: [
          'Palette — gruppi: Trigger, File, Contenuto, AI, Tabella, Bacheca, Flow; più pannello Template.',
          'Canvas — posiziona nodi, collega, sposta; Annulla / Ripeti accanto al titolo (anche Ctrl+Z / Ctrl+Y).',
          'Ispettore — configura trigger o nodo selezionato (cartella, tag, tabella, cron, ecc.).',
          'Esattamente un trigger. Di solito una catena; If / ramo può uscire in True e False.',
          '«Convalida» controlla i campi obbligatori e lo stream (file / righe) prima di Salvare.',
          'Tastiera: copia / incolla nodi (Ctrl+C / Ctrl+V), elimina selezione (Canc / Backspace) — vedi Scorciatoie.',
        ],
        tip: 'Cron: una volta, ogni giorno, ogni settimana (giorno + ora) o ogni mese (data + ora), con fuso orario.',
      },
      {
        title: 'Palette: Trigger',
        text: 'Esattamente un trigger per processo.',
        items: [
          'File caricato — parte al caricamento in una cartella osservata.',
          'Riga aggiunta — parte all\'aggiunta di una riga in tabella.',
          'Righe importate — parte dopo un import CSV/Excel.',
          'Cell matched — parte quando una cella assume il valore cercato.',
          'File moved — parte quando un file viene spostato nella cartella osservata.',
          'On schedule — parte con cron (il run inizia vuoto).',
        ],
      },
      {
        title: 'Palette: File',
        items: [
          'Applica tag — aggiunge tag ai file nel flusso.',
          'Rimuovi tag — toglie tag dai file nel flusso.',
          'Sposta file — sposta i file nella cartella di destinazione.',
          'Nel cestino — invia i file al cestino.',
          'Linked rows — trova le righe di tabella collegate ai file nel flusso.',
        ],
      },
      {
        title: 'Palette: Contenuto',
        items: [
          'Apply pattern — applica un pattern dello spazio alla scena di ogni file.',
          'Save as — salva una copia di ogni file nella cartella di destinazione.',
          'Replace content — sostituisce ogni file con il ritaglio di Remove background.',
        ],
      },
      {
        title: 'Palette: AI',
        items: [
          'Remove background — toglie lo sfondo a ogni immagine (token AI).',
          'Generate image — una nuova immagine per ogni file nel flusso.',
        ],
        tip: 'I nodi AI usano il pool condiviso di token nel profilo.',
      },
      {
        title: 'Palette: Tabella',
        items: [
          'Imposta celle — scrive valori nelle colonne scelte delle righe nel flusso.',
          'Insert row — aggiunge una nuova riga alla tabella.',
          'Linked files — carica nel flusso i file collegati alle righe.',
        ],
      },
      {
        title: 'Palette: Bacheca',
        items: [
          'Add priority — imposta la priorità sulle card nel flusso.',
          'Clear priority — rimuove la priorità dalle card nel flusso.',
          'Aggiungi assegnazione — assegna un membro (con scadenza/colonna) alle card.',
          'Unassign — rimuove gli assegnatari dalle card nel flusso.',
        ],
      },
      {
        title: 'Palette: Flow',
        items: [
          'Table source — fornisce righe di tabella a Generate image (ingresso data).',
          'Search — carica file dello spazio nel flusso per tag, nome o estensione.',
          'Filter — lascia solo gli elementi del flusso che soddisfano i criteri.',
          'Link — abbina i file ai valori del dizionario per nome (tag opzionale).',
          'Limit — lascia solo i primi N elementi del flusso.',
          'If / branch — indirizza il run a True o False senza cambiare lo stream.',
        ],
      },
      {
        title: 'Chi può cosa (sola lettura per editor)',
        items: [
          'Owner / Admin — creare, modificare, salvare, abilitare/disabilitare, eliminare e gestire i template.',
          'Editor / Viewer — catalogo e grafo in sola lettura (senza palette né Salva), più Esecuzioni.',
        ],
        tip: 'Gli editor possono studiare grafo ed esecuzioni — senza cambiare l\'automazione.',
      },
      {
        title: 'Esecuzioni / journal',
        items: [
          'Ogni run registra trigger e ogni step: ok, skipped o failed, con durata e dettagli.',
          'Apri un run — grafo con badge degli step e elenco degli step.',
          'Le esecuzioni sono visibili a ogni membro dello spazio.',
        ],
        where: 'Processo → Esecuzioni, o scheda del catalogo → Runs.',
      },
      {
        title: 'Template utente',
        items: [
          'Owner / Admin: menu Salva → «Salva come template» — grafo nella libreria dello spazio.',
          'Pannello Template (sinistra) — applica sul canvas (copia con nuovi id) o elimina.',
          'Applicare non crea un link live — modifichi la copia come qualsiasi processo.',
        ],
        tip: 'I template sono per spazio. Gli editor non possono crearli né applicarli.',
      },
    ],
  },
  notifications: {
    title: 'Notifiche e inviti',
    intro: 'La campanella nella barra superiore è la tua inbox per eventi del team e commenti.',
    blocks: [
      {
        title: 'Scheda Comments',
        items: [
          'Avvisi per nuovi commenti e risposte sui tuoi file.',
          'Avvisi per commenti sulle schede delle bacheche.',
          'Scadenze: presto (24 h), oggi, scaduto — se sei assegnatario.',
          'Digest Proprietario/Admin: qualcun altro sulle tue bacheche è in ritardo (circa una volta al giorno per assegnazione).',
          'Clic su un avviso file — apre l\'immagine e la scheda Commenti.',
          'Clic su un avviso scheda o scadenza — apre /boards/:id?card=:rowId con la modale.',
          '«Cancella tutto» — rimuovi tutte le notifiche del workspace corrente.',
        ],
        where: 'Campanella → scheda con icona messaggio (si apre per impostazione predefinita).',
      },
      {
        title: 'Scheda In arrivo',
        items: [
          'Inviti a unirti al workspace di qualcun altro.',
          'Accetta o rifiuta.',
          'Mostra: chi ha invitato, nome workspace, ruolo, scadenza.',
        ],
      },
      {
        title: 'Scheda Inviati',
        items: [
          'Inviti che hai inviato.',
          'Annulla un invito in sospeso.',
          '«Invita» — invita un nuovo utente.',
        ],
      },
      {
        title: 'Come invitare un utente',
        items: [
          'Campanella → Inviato → «Invita».',
          'Passo 1: trova l\'utente per email.',
          'Passo 2: scegli workspace e ruolo (Admin / Editore / Visualizzatore). Anche gli inviti in sospeso occupano un posto.',
          'Quando il team dello spazio è pieno, l\'invito è disabilitato finché un admin di sistema non alza il limite.',
          'Passo 3: facoltativamente nascondi cartelle, tabelle o board, poi invia l\'invito.',
        ],
      },
      {
        title: 'Badge sulla campanella',
        text: 'Il badge rosso mostra il totale di inviti in sospeso e avvisi commenti per il workspace corrente.',
      },
    ],
  },
  trash: {
    title: 'Cestino',
    intro: 'I file e le cartelle eliminati finiscono qui invece di sparire subito.',
    blocks: [
      {
        title: 'Come aprire',
        items: [
          'Fai clic su «Cestino» nella barra degli strumenti della schermata principale.',
          'Pulsante Indietro nell\'intestazione del cestino — torna alla galleria.',
        ],
        where: 'Icona cestino a destra nella barra degli strumenti.',
      },
      {
        title: 'Ripristina',
        items: [
          'Seleziona elementi → menu contestuale → «Ripristina».',
          'Scorciatoia: Ctrl+Alt+R.',
          'Gli elementi tornano alla posizione originale.',
        ],
      },
      {
        title: 'Elimina definitivamente',
        items: [
          '× su una scheda — elimina un file o una cartella per sempre (solo il proprietario).',
          '«Svuota cestino» — elimina tutto (con conferma, solo il proprietario).',
        ],
        tip: 'Dopo aver svuotato il cestino, i file non possono essere recuperati.',
      },
      {
        title: 'Limitazioni del cestino',
        items: [
          'Non puoi caricare file o creare cartelle.',
          'Non puoi trascinare e rilasciare elementi.',
          'L\'ordinamento non è disponibile.',
        ],
      },
    ],
  },
  shortcuts: {
    title: 'Scorciatoie e gesti',
    intro: 'Riferimento rapido. Premi Shift+? per aprire questo foglio di aiuto in qualsiasi momento. Le scorciatoie sono disabilitate quando l\'editor è aperto o il focus è in un campo di input.',
    blocks: [
      {
        title: 'Galleria',
        items: [
          'Ctrl+A — seleziona tutto.',
          'Ctrl+U — carica file.',
          'Ctrl+Shift+V — incolla screenshot dagli appunti.',
          'Ctrl+Alt+N — crea cartella.',
          'Ctrl+X — taglia.',
          'Ctrl+V — incolla.',
          'F2 — rinomina cartella (una selezionata).',
          'Ctrl+Alt+G — crea galleria (una cartella).',
          'Ctrl+Alt+T — aggiungi tag.',
          'Delete — sposta nel cestino.',
          'Ctrl+Alt+R — ripristina (nel cestino).',
          'Escape — annulla selezione.',
        ],
      },
      {
        title: 'Visualizzatore file',
        items: [
          '← / → — file precedente / successivo.',
          'Escape — chiudi finestra.',
        ],
      },
      {
        title: 'Editor',
        items: [
          'Ctrl+Z — annulla.',
          'Ctrl+Y — ripeti.',
          'Ctrl+0 — adatta allo schermo.',
          'Ctrl++ — ingrandisci.',
          'Ctrl+- — rimpicciolisci.',
          'H — capovolgi orizzontale (immagine selezionata).',
          'V — capovolgi verticale (immagine selezionata).',
          'T — modalità raddrizza.',
          'C — ritaglio libero.',
          'L — lazo: elimina selezione.',
          'Shift+L — lazo: mantieni solo la selezione.',
          'Ctrl+C — copia livello selezionato (non il frame).',
          'Ctrl+V — incolla livello.',
          'Delete / Backspace — elimina livello selezionato (testo, immagine o forma).',
          'Escape — esci dalla modalità corrente (rapporto d\'aspetto, ridimensionamento, strumento forma, ritaglio, lazo) o ripristina le modifiche.',
        ],
        tip: 'I suggerimenti delle scorciatoie compaiono nei tooltip dei pulsanti dell\'editor (su desktop con mouse). Il rapporto d\'aspetto del frame e gli strumenti livello non hanno scorciatoie con lettere — usa il pannello proprietà.',
      },
      {
        title: 'Tabelle (griglia CRM)',
        items: [
          'Invio — salva cella o avvia modifica.',
          'Esc — annulla modifica cella.',
          'Tab / Maiusc+Tab — cella successiva / precedente.',
        ],
        where: 'Apri una tabella dalla barra laterale.',
      },
      {
        title: 'Editor processi (workflow)',
        items: [
          'Ctrl+Z — annulla.',
          'Ctrl+Y o Ctrl+Maiusc+Z — ripeti.',
          'Ctrl+C — copia i nodi selezionati.',
          'Ctrl+V — incolla i nodi.',
          'Canc / Backspace — elimina i nodi selezionati.',
        ],
        where: 'Apri un processo dalla barra laterale (Workflow).',
        tip: 'Le scorciatoie usano i tasti fisici (anche su layout non latini). Disattivate se il focus è in un campo o il processo è in sola lettura.',
      },
      {
        title: 'Gesti touch',
        items: [
          'Tocco singolo — apri; doppio tocco — seleziona.',
          'Rubber band — selezione multipla.',
          'Pressione prolungata — menu contestuale.',
          'Pinch nell\'editor — zoom.',
        ],
      },
    ],
  },
};
